import React, { useState, useEffect, useRef } from "react";
import { useCart } from "../context/CartContext";
import { useTour } from "../context/TourContext";
import { FaCarSide, FaBus } from "react-icons/fa";
import { MdAirportShuttle } from "react-icons/md";
import { createBooking, getTourDetail } from "../common/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import CheckoutModal from "./CheckoutModal";
import { useTourSSE } from "../hook/useTourSSE";
import Calendar from "./Calendar";

const MAX_CAPACITY = 45;

const getVehicle = (count) => {
  if (count <= 3) return { icon: FaCarSide, label: "Van", max: 3 };
  if (count <= 12) return { icon: MdAirportShuttle, label: "Minibus", max: 12 };
  return { icon: FaBus, label: "Bus", max: 20 };
};

function getToday() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseLocalDate(dateString) {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatDisplayDate(dateString) {
  return parseLocalDate(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatKRW(n) {
  return `₩${Number(n).toLocaleString("ko-KR")}`;
}

// 이미지 슬라이더
function ImageSlider({ images, fallbackSrc }) {
  const [current, setCurrent] = useState(0);
  const dragStartX = useRef(null);
  const isDragging = useRef(false);  // 추가

  const list = images?.length ? images : (fallbackSrc ? [{ image_url: fallbackSrc, alt_text: "" }] : []);

  function go(idx) {
    setCurrent(Math.max(0, Math.min(idx, list.length - 1)));
  }

  function onMouseDown(e) {
    dragStartX.current = e.clientX;
    isDragging.current = false;
  }

  function onMouseUp(e) {
    if (dragStartX.current === null) return;
    const diff = dragStartX.current - e.clientX;
    if (Math.abs(diff) > 50) {
      isDragging.current = true;
      go(current + (diff > 0 ? 1 : -1));
    }
    dragStartX.current = null;
  }

  function onMouseLeave() {
    dragStartX.current = null; // 슬라이더 밖으로 나가면 초기화
  }

  function onClick(e) {
    if (isDragging.current) {
      e.stopPropagation();
      isDragging.current = false;
    }
  }

  function onTouchStart(e) {
    dragStartX.current = e.touches[0].clientX;
    isDragging.current = false;
  }

  function onTouchEnd(e) {
    if (dragStartX.current === null) return;
    const diff = dragStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      isDragging.current = true;
      go(current + (diff > 0 ? 1 : -1));
    }
    dragStartX.current = null;
  }

  if (!list.length) {
    return <div className="w-full aspect-video bg-gray-100 animate-pulse" />;
  }

  return (
    <div
      className="relative w-full aspect-video overflow-hidden cursor-grab active:cursor-grabbing select-none"
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onClick={onClick}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="flex h-full transition-transform duration-500 ease-in-out"
        style={{ transform: `translateX(-${current * 100}%)` }}
      >
        {list.map((img, i) => (
          <img
            key={i}
            src={img.image_url}
            alt={img.alt_text || ""}
            className="shrink-0 object-cover object-center pointer-events-none"
            style={{ minWidth: "100%", width: "100%", height: "100%" }}
            draggable={false}
          />
        ))}
      </div>

      {list.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {list.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); go(i); }}
              className={[
                "h-1.5 rounded-full transition-all duration-200",
                i === current ? "bg-white w-3" : "bg-white/50 w-1.5",
              ].join(" ")}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// 루트 타임라인 스켈레톤
function RouteSkeleton() {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-3 items-center">
          <div className="w-2.5 h-2.5 rounded-full bg-gray-200 shrink-0" />
          <div className="flex flex-col gap-1 flex-1">
            <div className="h-2.5 w-10 bg-gray-200 rounded" />
            <div className="h-3.5 w-32 bg-gray-200 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TourCard({
  tags = [],
  className = "",
  images = [],
  imgSrc,
  title = "Nami Island Day Tour",
  description = "",
  participantCount: initialParticipantCount = 0,
  participantLabel = "Participant",
  width = 900,
  selectedDate = null,
  tourId = "",
  onBook,
  onCartAdd,
  defaultOpen = false,
  standalone = false,
}) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [displayParticipantCount, setDisplayParticipantCount] = useState(initialParticipantCount);
  const [persons, setPersons] = useState(1);

  const [open, setOpen] = useState(defaultOpen);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [localDate, setLocalDate] = useState(null);
  const date = localDate ?? selectedDate ?? getToday();

  const [optionValue, setOption] = useState(0);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  const totalPersons = Number(displayParticipantCount) + persons;

  const participantCount = displayParticipantCount;
  const vehicle = getVehicle(displayParticipantCount);
  const VehicleIcon = vehicle.icon;
  const remaining = MAX_CAPACITY - displayParticipantCount;

  const panelVehicle = getVehicle(totalPersons);
  const PanelVehicleIcon = panelVehicle.icon;

  const { addToCart } = useCart();
  const { addParticipant } = useTour();

  const basePrice = detail?.basePrice ?? 0;
  const discountedPrice = calcPerPerson(basePrice, totalPersons, detail);
  const perPerson = discountedPrice + optionValue;
  const total = perPerson * persons;

  const discountRate = totalPersons <= 3 ? 0
    : totalPersons <= 12 ? (detail?.minibusDiscount ?? 10)
      : (detail?.busDiscount ?? 20);

  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => {
    setLocalDate(null); // 날짜 바뀌면 사용자 입력 초기화
  }, [selectedDate]);

  useEffect(() => {
    if (!calendarOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") setCalendarOpen(false);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [calendarOpen]);

  useEffect(() => {
    setDisplayParticipantCount(initialParticipantCount);
  }, [initialParticipantCount]);

  // 토스트 자동 소멸
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  // 패널 열릴 때 최초 1회 fetch — error 있으면 재호출 차단
  useEffect(() => {
    if (detail || loading || error) return;

    setLoading(true);

    getTourDetail(tourId, date)
      .then((data) => {
        setDetail(data);
        setDisplayParticipantCount(data.participantCount);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [open, tourId, date, detail, loading, error]);

  function handleDateSelect(d) {
    const dateString = [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, "0"),
      String(d.getDate()).padStart(2, "0"),
    ].join("-");

    setLocalDate(dateString);
    setDetail(null);
    setError(null);
    setCalendarOpen(false);
  }

  function calcPerPerson(basePrice, totalPersons, detail) {
    if (!detail || !basePrice) return basePrice;
    if (totalPersons <= 3) return basePrice;
    if (totalPersons <= 12) return Math.ceil(basePrice * (1 - (detail.minibusDiscount ?? 10) / 100));
    return Math.ceil(basePrice * (1 - (detail.busDiscount ?? 20) / 100));
  }

  async function handleCartAdd() {
    if (!date) { setToast("Please select a date"); return; }
    if (!user) { navigate("/login"); return; }
    try {
      await addToCart({ tourId, title, imgSrc: detail?.images?.[0]?.image_url ?? imgSrc, date, persons, optionValue, perPerson, total });
      onCartAdd?.({ date, persons, optionValue, total });
      setToast(`🛒 Added to Cart — ${date} / ${persons} person(s)`);
    } catch {
      setToast("Unable to add this tour. Please try again.");
    }
  }

  function handleBook() {
    if (!user) { navigate("/login"); return; }
    if (!date) { setToast("Please select a date"); return; }
    if (persons < 1) { setToast("Please enter at least 1 person"); return; }
    if (persons > remaining) { setToast(`Only ${remaining} seats left`); return; }
    navigate("/checkout", {
      state: { tourId, title, date, persons, perPerson, total, optionValue }
    });
  }

  const [personsInput, setPersonsInput] = useState("1");

  return (
    <div
      className={["bg-white rounded-[36px] shadow-2xl overflow-hidden", className].join(" ")}
      style={{ maxWidth: width }}
    >
      {/* ── 상단 카드 ── */}
      <div
        className="relative"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      >
        <div className="grid grid-cols-2 md:grid-cols-1">

          {/* 이미지 슬라이더 */}
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <ImageSlider images={images} fallbackSrc={imgSrc} />
            <div className="absolute top-[10px] left-[10px] w-[80px] h-[42px]">
              {tags.length > 0 && (
                <div className="absolute top-4 left-4 flex flex-row gap-1.5">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="w-fit h-fit whitespace-nowrap px-3 py-1 rounded-full text-[12px] font-semibold bg-black/40 text-white backdrop-blur-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 텍스트 + 뱃지 */}
          <div className="relative pt-[60px] pl-[40px] pr-[110px] md:pt-[24px] md:pl-[24px] md:pr-[24px] md:pb-[80px]">
            <h2 className="text-[32px] md:text-[22px] font-bold text-black">{title}</h2>
            {!standalone && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/tours/${tourId}?date=${date}`);
                }}
                className="mt-2 border-0 bg-transparent p-0 text-[12px] font-semibold text-[#E8876A] hover:text-[#D96F52]"
              >
                View tour page &rarr;
              </button>
            )}
            <p className="mt-[32px] md:mt-[12px] text-[14px] text-[#555555] leading-relaxed md:pr-[80px]">
              {description}
            </p>

            {/* 뱃지 — 카드 래퍼 기준 고정 */}
            <div className="absolute bottom-[40px] right-[36px] md:bottom-[16px] md:right-[24px] flex flex-col gap-[8px] items-center pointer-events-none">
              <div className="relative">
                <div className="w-[64px] h-[64px] md:w-[48px] md:h-[48px] rounded-2xl bg-gray-100 flex items-center justify-center">
                  <VehicleIcon size={32} className="text-black" />
                </div>
                <div className="absolute -top-1 -right-2 w-[22px] h-[22px] rounded-full bg-[#ff8564] flex items-center justify-center text-white font-bold text-[11px]">
                  {participantCount}
                </div>
              </div>
              <div className="text-black font-bold text-sm">{vehicle.label}</div>
              <div className={["text-[11px] font-semibold", remaining <= 3 ? "text-red-400" : "text-gray-400"].join(" ")}>
                {remaining} seats left
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── 펼침 패널 ── */}
      <div
        className={[
          "grid transition-all duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        ].join(" ")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="overflow-hidden">
          <div className="flex md:flex-col">

            {/* 좌: 루트 타임라인 */}
            <div className="w-[45%] md:w-full shrink-0 flex flex-col gap-4 px-6 py-5 border-r md:border-r-0 md:border-b border-gray-200">
              <p className="text-[11px] font-semibold tracking-widest text-gray-400 uppercase m-0">
                Route
              </p>

              {loading && <RouteSkeleton />}

              {error && !loading && (
                <div className="flex flex-col gap-2">
                  <p className="text-[12px] text-gray-400 m-0">⚠️ {error}</p>
                  <button
                    className="self-start text-[12px] text-[#E8876A] border border-[#E8876A] rounded-full px-3 py-0.5 hover:bg-[#E8876A]/10 transition-colors"
                    onClick={() => setError(null)}
                  >
                    Retry
                  </button>
                </div>
              )}

              {detail && !loading && !error && (
                <div className="flex flex-col">
                  {detail.stops.map((stop, i) => (
                    <div key={i} className="flex gap-3 items-stretch">
                      <div className="flex flex-col items-center">
                        <div className={[
                          "w-2.5 h-2.5 rounded-full shrink-0 mt-0.5",
                          i === 0 || i === detail.stops.length - 1 ? "bg-[#E8876A]" : "bg-gray-300",
                        ].join(" ")} />
                        {i < detail.stops.length - 1 && (
                          <div className="w-[1.5px] flex-1 bg-gray-200 my-1" />
                        )}
                      </div>
                      <div className="pb-3">
                        <div className="text-[11px] text-gray-400 leading-none">{stop.time}</div>
                        <div className="text-[13px] font-semibold text-gray-700 mt-0.5">{stop.place}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 우: 예약 폼 */}
            <div className="flex-1 flex flex-col gap-3.5 px-6 py-5">
              <p className="text-[11px] font-semibold tracking-widest text-gray-400 uppercase m-0">
                Reservation Info
              </p>

              {detail && (
                <div className="flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-gray-50 border border-gray-200">
                  <div className="flex items-center gap-2">
                    <PanelVehicleIcon size={18} className="text-gray-600" />
                    <span className="text-[13px] font-medium text-gray-700">{panelVehicle.label}</span>
                    {discountRate > 0 && (
                      <span className="text-[11px] font-semibold text-[#E8876A] bg-[#E8876A]/10 px-2 py-0.5 rounded-full">
                        {discountRate}% off
                      </span>
                    )}
                  </div>
                  <div className="text-[13px] text-gray-500">
                    <span className={totalPersons >= MAX_CAPACITY ? "text-red-400 font-semibold" : "text-gray-800 font-semibold"}>
                      {totalPersons}
                    </span>
                    <span className="text-gray-400"> / {MAX_CAPACITY}</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2.5">
                <div className="flex flex-col gap-1">
                  <span className="text-[12px] text-gray-400">Date</span>
                  <button
                    type="button"
                    onClick={() => setCalendarOpen(true)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 rounded-[10px] border border-gray-300 text-[13px] text-gray-800 bg-white outline-none hover:border-[#E8876A] focus:border-[#E8876A] focus:ring-2 focus:ring-[#E8876A]/20 transition-colors"
                    aria-haspopup="dialog"
                  >
                    <span className="truncate">{formatDisplayDate(date)}</span>
                    <span aria-hidden="true" className="text-[#E8876A]">&#128197;</span>
                  </button>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[12px] text-gray-400">Person(s)</label>
                  <input
                    type="number"
                    value={personsInput}
                    min={0}
                    max={remaining}
                    onChange={(e) => {
                      setPersonsInput(e.target.value);
                      const num = Number(e.target.value);
                      if (!isNaN(num)) setPersons(Math.max(0, Math.min(remaining, num)));
                    }}
                    onBlur={() => {
                      if (personsInput === "" || personsInput === "0") {
                        setPersonsInput("1");
                        setPersons(1);
                      }
                    }}
                    className="px-3 py-2 rounded-[10px] border border-gray-300 text-[13px] text-gray-800 bg-white outline-none focus:border-[#E8876A] focus:ring-2 focus:ring-[#E8876A]/20 w-full"
                  />
                </div>
              </div>

              {!detail ? (
                <div className="h-[58px] rounded-xl bg-gray-100 animate-pulse" />
              ) : (
                <div className="flex justify-between items-center px-3.5 py-3 rounded-xl bg-gray-50 border border-gray-200">
                  <div>
                    <p className="text-[12px] text-gray-400 m-0">Total Price</p>
                    <p className="text-[11px] text-gray-300 mt-0.5 m-0">
                      {formatKRW(perPerson)} × {persons}
                      {discountRate > 0 && (
                        <span className="ml-1 text-[#E8876A]">({discountRate}% off)</span>
                      )}
                    </p>
                  </div>
                  <p className="text-[18px] font-bold text-gray-900 m-0">
                    {formatKRW(total)}{" "}
                    <span className="text-[12px] font-normal text-gray-400">KRW</span>
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  className="flex-1 py-2.5 rounded-[10px] border border-gray-300 bg-white text-[13px] font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={handleCartAdd}
                >
                  🛒 Cart
                </button>
                <button
                  className="flex-[2] py-2.5 rounded-[10px] bg-[#E8876A] text-white text-[13px] font-semibold hover:bg-[#D96F52] transition-colors cursor-pointer border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleBook}
                  disabled={!detail}
                >
                  Checkout →
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        tourId={tourId}
        title={title}
        date={date}
        persons={persons}
        perPerson={perPerson}
        total={total}
        optionValue={optionValue}
      />

      {calendarOpen && (
        <div
          className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) setCalendarOpen(false);
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Select a tour date"
        >
          <div className="relative w-full max-w-[900px]">
            <button
              type="button"
              onClick={() => setCalendarOpen(false)}
              className="absolute -right-3 -top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-[#E8876A] text-xl font-bold text-white shadow-lg transition-colors hover:bg-[#D96F52] focus:outline-none focus:ring-2 focus:ring-[#E8876A]/40 focus:ring-offset-2"
              aria-label="Close calendar"
            >
              &times;
            </button>
            <div className="max-h-[calc(100vh-3rem)] overflow-y-auto rounded-[28px] bg-white shadow-2xl">
              <Calendar
                initialDate={parseLocalDate(date)}
                onChange={handleDateSelect}
                embedded
              />
            </div>
          </div>
        </div>
      )}

      {/* 토스트 */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#2C2C2A] text-white px-5 py-2.5 rounded-full text-[13px] z-[2000] whitespace-nowrap pointer-events-none">
          {toast}
        </div>
      )}
    </div>
  );
}
